
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTableAuth } from '../hooks/useTableAuth';
import { useNavigate } from 'react-router-dom';
import VideoCarousel from '../components/VideoCarousel';
import NotificationBanner from '../components/NotificationBanner';
import { trackClickCta } from '../analytics/analytics';
//import Testimonials from '../components/Testimonials';

/* ------------------------------------------------------------------ */
/* Reveal: anima la entrada de cada sección al hacer scroll            */
/* ------------------------------------------------------------------ */
function Reveal({ children, delay = 0, className = '' }: { children: ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Íconos SVG (estilo lucide, stroke)                                  */
/* ------------------------------------------------------------------ */
const iconProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  viewBox: '0 0 24 24',
};

const Icons = {
  qr: (
    <svg {...iconProps}><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3v3h-3zM21 14v.01M14 21v.01M17.5 17.5h3.5v3.5"/></svg>
  ),
  realtime: (
    <svg {...iconProps}><path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/><path d="M12 7v5l3 3"/></svg>
  ),
  promo: (
    <svg {...iconProps}><path d="M20.59 13.41 12 22 2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><circle cx="7.5" cy="7.5" r="1.5"/></svg>
  ),
  easy: (
    <svg {...iconProps}><path d="M8 13V9a4 4 0 0 1 8 0v4"/><path d="M6 13h12l-1 8H7l-1-8z"/></svg>
  ),
  team: (
    <svg {...iconProps}><path d="M17 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9.5" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
  ),
  payments: (
    <svg {...iconProps}><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/><path d="M6 15h4"/></svg>
  ),
  ai: (
    <svg {...iconProps}><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1"/><circle cx="12" cy="12" r="4"/></svg>
  ),
  tables: (
    <svg {...iconProps}><path d="M3 8h18"/><path d="M5 8v10M19 8v10"/><path d="M8 8V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v3"/><path d="M3 18h4M17 18h4"/></svg>
  ),
  growth: (
    <svg {...iconProps}><path d="M3 17l6-6 4 4 8-8"/><path d="M21 7v5h-5"/></svg>
  ),
  clock: (
    <svg {...iconProps}><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
  ),
  target: (
    <svg {...iconProps}><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><circle cx="12" cy="12" r="1"/></svg>
  ),
  smile: (
    <svg {...iconProps}><circle cx="12" cy="12" r="9"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><path d="M9 9h.01M15 9h.01"/></svg>
  ),
  chart: (
    <svg {...iconProps}><path d="M3 3v18h18"/><rect x="7" y="12" width="3" height="6" rx="0.5"/><rect x="12" y="8" width="3" height="10" rx="0.5"/><rect x="17" y="5" width="3" height="13" rx="0.5"/></svg>
  ),
  rocket: (
    <svg {...iconProps}><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/></svg>
  ),
  check: (
    <svg {...iconProps}><path d="M20 6 9 17l-5-5"/></svg>
  ),
  arrowRight: (
    <svg {...iconProps}><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
  ),
};

/* ------------------------------------------------------------------ */
/* Datos de secciones                                                  */
/* ------------------------------------------------------------------ */
const FEATURES = [
  {
    icon: Icons.qr,
    title: 'Menús digitales interactivos',
    highlight: true,
    description:
      'Tus comensales escanean el QR de la mesa y ordenan al instante, sin esperar a que un mesero esté disponible. La orden llega directo a cocina y el mesero recibe la notificación para entregarla.',
  },
  {
    icon: Icons.realtime,
    title: 'Seguimiento en tiempo real',
    description:
      'Los pedidos llegan directamente a la cocina y todos — comensal, mesero y cocina — ven el estado de la orden en tiempo real.',
  },
  {
    icon: Icons.promo,
    title: 'Promociones y descuentos',
    description:
      'Crea promociones desde el panel de administración y aparecerán automáticamente en el menú digital, listas para que tus clientes las apliquen a su orden.',
  },
  {
    icon: Icons.easy,
    title: 'Fácil de usar',
    description:
      'Sin configuraciones complejas: escanear el QR de la mesa y ordenar. No necesitas un sistema POS complicado para operar.',
  },
  {
    icon: Icons.team,
    title: 'Gestión de personal',
    description:
      'Asigna gerentes y jefes de cocina a cada restaurante, con visibilidad completa del estado de las órdenes, los pagos y los clientes.',
  },
  {
    icon: Icons.payments,
    title: 'Registro de pagos',
    description:
      'Registra las ventas y lleva el control de tus ingresos sin afectar tu sistema POS actual. Una forma sencilla de modernizar tu restaurante.',
  },
  {
    icon: Icons.ai,
    title: 'Consejos inteligentes',
    description:
      'Recomendaciones generadas con Inteligencia Artificial que analizan los datos de tu restaurante para ayudarte a tomar decisiones informadas.',
  },
  {
    icon: Icons.tables,
    title: 'Gestión de mesas',
    highlight: true,
    description:
      'Controla el estado de tus mesas en tiempo real, asigna meseros y recibe notificaciones dirigidas. Optimiza la rotación de clientes para maximizar tus ventas.',
  },
];

const BENEFITS = [
  { icon: Icons.growth, stat: '+25%', title: 'Aumenta tus ventas', description: 'Menús atractivos y pedidos sin errores elevan el ticket promedio.' },
  { icon: Icons.clock, stat: '10+ hrs', title: 'Ahorra cada semana', description: 'Automatiza procesos manuales y enfócate en la experiencia del cliente.' },
  { icon: Icons.target, stat: '30 días', title: 'ROI acelerado', description: 'Recupera tu inversión rápidamente con el aumento en eficiencia.' },
  { icon: Icons.smile, stat: '100%', title: 'Clientes satisfechos', description: 'Una experiencia moderna y sin fricciones que mejora la percepción de tu marca.' },
  { icon: Icons.chart, stat: '24/7', title: 'Datos valiosos', description: 'Analytics detallados para tomar decisiones informadas sobre tu negocio.' },
  { icon: Icons.rocket, stat: '∞', title: 'Escalabilidad', description: 'Crece sin límites: desde un local hasta una cadena de restaurantes.' },
];

const STEPS = [
  {
    title: 'Sube tu menú',
    description: 'Crea tu menú digital con fotos, precios y categorías en minutos. Sin apps que instalar.',
  },
  {
    title: 'Comparte tu enlace por WhatsApp',
    description: 'Envía el link de tu menú a tus clientes, ponlo en tu estado o en tus grupos. También puedes generar códigos QR para tus mesas.',
  },
  {
    title: 'Recibe pedidos claros y completos',
    description: 'Tus clientes arman su pedido solos desde el menú y tú lo ves llegar organizado en tu panel. Sin mensajes perdidos.',
  },
];

const DEMO_MENU_URL = '/menu?restaurantId=518c3d4c-83ae-11f1-a526-b58a4e45e408';

export default function HomePage() {
  const { user, isLoading } = useAuth();
  const { tableData } = useTableAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d1b26] flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-[#22B6B2]/20 border-t-[#22B6B2] rounded-full animate-spin mx-auto mb-6"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-white/40 rounded-full animate-spin mx-auto" style={{ animationDelay: '0.5s' }}></div>
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Cargando...</h3>
          <p className="text-[#8fa3b0]">Preparando todo para ti</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f8fa] text-[#2C4358]">
      {/* Banner de Notificaciones */}
      <NotificationBanner
        position="top"
        onPermissionGranted={() => console.log('✅ Notificaciones habilitadas en HomePage')}
        onPermissionDenied={() => console.log('❌ Permisos denegados en HomePage')}
      />

      {/* ============================ NAVBAR ============================ */}
      <header
        className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 ${
          scrolled
            ? 'bg-[#0d1b26]/85 backdrop-blur-xl shadow-lg shadow-black/10 border-b border-white/10'
            : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              type="button"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2.5 group"
            >
              <img
                src="/assets/ots/nobackground/Servicio_a_Tiempo_icon_64x64.png"
                alt="Servicio a Tiempo"
                className="w-9 h-9 object-contain transition-transform duration-300 group-hover:scale-110"
              />
              <span className="text-white font-bold tracking-tight text-lg hidden sm:block">
                Servicio <span className="text-[#22B6B2]">a Tiempo</span>
              </span>
            </button>

            <nav className="hidden md:flex items-center gap-8">
              {[
                { id: 'pedidos-whatsapp', label: 'Pedidos por WhatsApp' },
                { id: 'como-funciona', label: 'Cómo funciona' },
                { id: 'funciones', label: 'Funciones' },
                { id: 'beneficios', label: 'Beneficios' },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="text-sm font-medium text-white/70 hover:text-white transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-3">
              {user ? (
                <button
                  onClick={() => {
                    trackClickCta('ir_a_perfil', 'home_navbar', '/profile');
                    navigate('/profile');
                  }}
                  className="px-5 py-2 text-sm font-semibold text-[#0d1b26] bg-[#22B6B2] hover:bg-[#2cd0cb] rounded-full transition-colors shadow-lg shadow-[#22B6B2]/25"
                >
                  Mi Perfil
                </button>
              ) : (
                <>
                  <button
                    onClick={() => {
                      trackClickCta('iniciar_sesion', 'home_navbar', '/login');
                      navigate('/login');
                    }}
                    className="hidden sm:block px-4 py-2 text-sm font-medium text-white/80 hover:text-white transition-colors"
                  >
                    Iniciar sesión
                  </button>
                  <button
                    onClick={() => {
                      trackClickCta('soy_restaurante', 'home_navbar', '/login?returnUrl=/pricing&role=owner');
                      navigate('/login?returnUrl=/pricing&role=owner');
                    }}
                    className="px-5 py-2 text-sm font-semibold text-[#0d1b26] bg-[#22B6B2] hover:bg-[#2cd0cb] rounded-full transition-colors shadow-lg shadow-[#22B6B2]/25"
                  >
                    Comenzar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ============================ HERO ============================ */}
      <section className="relative overflow-hidden bg-[#0d1b26]">
        {/* Glows y textura */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-[#22B6B2]/15 blur-[140px]" />
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-[#2C4358]/60 blur-[120px]" />
          <div className="absolute bottom-0 -right-40 w-[500px] h-[500px] rounded-full bg-[#22B6B2]/10 blur-[120px]" />
          <div
            className="absolute inset-0 opacity-[0.35]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)',
              backgroundSize: '56px 56px',
              maskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent)',
              WebkitMaskImage: 'radial-gradient(ellipse 80% 60% at 50% 30%, black, transparent)',
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 md:pt-40 md:pb-24">
          <div className="text-center">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#22B6B2]/30 bg-[#22B6B2]/10 text-[#5fd6d2] text-sm font-medium mb-8 backdrop-blur">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#22B6B2] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#22B6B2]"></span>
                </span>
                La plataforma digital para restaurantes modernos
              </div>
            </Reveal>

            <Reveal delay={100}>
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-white mb-6 leading-[1.05]">
                Revoluciona tu
                <span className="block bg-gradient-to-r from-[#22B6B2] via-[#4fd8d4] to-[#22B6B2] bg-clip-text text-transparent pb-2">
                  Restaurante
                </span>
              </h1>
            </Reveal>

            <Reveal delay={200}>
              <p className="text-lg md:text-2xl text-[#9db2bf] max-w-3xl mx-auto mb-10 leading-relaxed">
                <span className="font-semibold text-[#5fd6d2]">Menús QR interactivos</span>, pedidos{' '}
                <span className="text-white font-medium">en sitio</span> o{' '}
                <span className="text-white font-medium">por WhatsApp</span>, y gestión completa de tu
                operación, todo en una sola plataforma.
              </p>
            </Reveal>

            {/* CTA Buttons */}
            <Reveal delay={300}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                {user ? (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => {
                        trackClickCta('ir_a_perfil', 'home_hero', '/profile');
                        navigate('/profile');
                      }}
                      className="group inline-flex items-center gap-2 px-8 py-4 bg-[#22B6B2] hover:bg-[#2cd0cb] text-[#0d1b26] font-bold rounded-full shadow-xl shadow-[#22B6B2]/30 hover:shadow-[#22B6B2]/50 transform hover:-translate-y-0.5 transition-all duration-300"
                    >
                      Ir a Perfil
                      <span className="w-4 h-4 transition-transform group-hover:translate-x-1">{Icons.arrowRight}</span>
                    </button>
                    {tableData && (
                      <button
                        onClick={() => {
                          trackClickCta('ver_menu', 'home_hero', '/menu');
                          navigate('/menu');
                        }}
                        className="px-8 py-4 bg-white/5 text-white font-semibold rounded-full border border-white/15 hover:bg-white/10 hover:border-[#22B6B2]/50 backdrop-blur transition-all duration-300"
                      >
                        🍽️ Ver Menú
                      </button>
                    )}
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        trackClickCta(
                          'soy_restaurante',
                          'home_hero',
                          '/login?returnUrl=/pricing&role=owner'
                        );
                        navigate('/login?returnUrl=/pricing&role=owner');
                      }}
                      className="group inline-flex items-center gap-2 px-8 py-4 bg-[#22B6B2] hover:bg-[#2cd0cb] text-[#0d1b26] font-bold rounded-full shadow-xl shadow-[#22B6B2]/30 hover:shadow-[#22B6B2]/50 transform hover:-translate-y-0.5 transition-all duration-300"
                    >
                      Soy Restaurante
                      <span className="w-4 h-4 transition-transform group-hover:translate-x-1">{Icons.arrowRight}</span>
                    </button>
                    <button
                      onClick={() => {
                        trackClickCta('soy_comensal', 'home_hero', '/login?role=comensal');
                        navigate('/login?role=comensal');
                      }}
                      className="px-8 py-4 bg-white/5 text-white font-semibold rounded-full border border-white/15 hover:bg-white/10 hover:border-[#22B6B2]/50 backdrop-blur transition-all duration-300"
                    >
                      Soy Comensal
                    </button>
                  </>
                )}
              </div>
            </Reveal>

            {!user && (
              <Reveal delay={380}>
                <button
                  onClick={() => {
                    trackClickCta('iniciar_sesion', 'home_hero', '/login');
                    navigate('/login');
                  }}
                  className="text-sm text-[#9db2bf] hover:text-white transition-colors mb-12 underline underline-offset-4 decoration-[#22B6B2]/50"
                >
                  ¿Ya tienes cuenta? Inicia sesión
                </button>
              </Reveal>
            )}

            {/* Métricas de confianza */}
            <Reveal delay={450}>
              <div className="flex flex-wrap justify-center gap-x-12 gap-y-6 mb-16">
                {[
                  { value: '+25%', label: 'en ventas' },
                  { value: '10+ hrs', label: 'ahorradas por semana' },
                  { value: '30 días', label: 'para recuperar tu inversión' },
                ].map((stat) => (
                  <div key={stat.label} className="text-center">
                    <div className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-[#22B6B2] to-[#4fd8d4] bg-clip-text text-transparent">
                      {stat.value}
                    </div>
                    <div className="text-sm text-[#9db2bf] mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Video Section */}
            <Reveal delay={550}>
              <VideoCarousel
                items={[
                  {
                    url: '/assets/videos/menu.mp4',
                    title: 'Moderniza tu restaurante',
                    description: 'Mira cómo funciona Servicio a Tiempo',
                  },
                  {
                    url: '/assets/videos/menu2.mp4',
                    title: 'Más ejemplos de la experiencia',
                    description: 'Explora otra vista de la plataforma en acción',
                  },
                ]}
              />
            </Reveal>
          </div>
        </div>

        {/* Transición curva hacia la siguiente sección */}
        <div className="relative h-16 md:h-24">
          <svg className="absolute bottom-0 w-full h-full text-[#f6f8fa]" viewBox="0 0 1440 96" preserveAspectRatio="none" fill="currentColor">
            <path d="M0 96h1440V32c-211 42-464 64-720 64S211 74 0 32v64z" />
          </svg>
        </div>
      </section>

      {/* ==================== PEDIDOS POR WHATSAPP ==================== */}
      <section id="pedidos-whatsapp" className="py-24 scroll-mt-20 relative overflow-hidden">
        <div className="absolute -top-20 -left-40 w-[500px] h-[500px] rounded-full bg-[#25D366]/5 blur-[120px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#25D366]/10 text-[#1a9e4b] text-sm font-semibold mb-4">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                ¿Recibes pedidos por WhatsApp?
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#2C4358] mb-5">
                Convierte el caos del chat
                <span className="block bg-gradient-to-r from-[#25D366] to-[#22B6B2] bg-clip-text text-transparent pb-1">
                  en pedidos organizados
                </span>
              </h2>
              <p className="text-xl text-[#7b8b99] max-w-3xl mx-auto">
                Deja de tomar pedidos mensaje por mensaje. Comparte el enlace de tu menú por WhatsApp y
                tus clientes arman su pedido solos, sin errores y sin estresar a tu equipo.
              </p>
            </div>
          </Reveal>

          {/* Comparativa Antes / Ahora */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-14 max-w-5xl mx-auto">
            {/* Antes */}
            <Reveal>
              <div className="h-full bg-white rounded-3xl border border-[#e6ebf0] p-6 md:p-8 relative">
                <span className="inline-block px-3 py-1 rounded-full bg-red-50 text-red-500 text-xs font-bold uppercase tracking-wide mb-6">
                  Antes · pedidos por chat
                </span>
                <div className="space-y-3">
                  <div className="max-w-[80%] bg-[#f0f2f5] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-[#2C4358]">
                    Hola, ¿me mandas el menú? 🙏
                  </div>
                  <div className="max-w-[80%] bg-[#f0f2f5] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-[#2C4358]">
                    ¿Sí tienen el especial de hoy?
                  </div>
                  <div className="max-w-[80%] ml-auto bg-[#d9fdd3] rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm text-[#2C4358]">
                    Perdón, ¿me repites tu pedido? 😅
                  </div>
                  <div className="max-w-[80%] bg-[#f0f2f5] rounded-2xl rounded-tl-sm px-4 py-2.5 text-sm text-[#2C4358]">
                    Eran 2 tacos… ¡sin cebolla porfa!
                  </div>
                  <div className="max-w-[80%] ml-auto bg-[#d9fdd3] rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm text-[#2C4358]">
                    ¿Ese era con todo o el otro? 🤔
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm text-red-500 font-medium">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold">47</span>
                  mensajes sin leer, pedidos perdidos y un equipo estresado
                </div>
              </div>
            </Reveal>

            {/* Ahora */}
            <Reveal delay={150}>
              <div className="h-full bg-gradient-to-br from-[#0d1b26] to-[#2C4358] rounded-3xl p-6 md:p-8 shadow-xl shadow-[#2C4358]/20 relative">
                <span className="inline-block px-3 py-1 rounded-full bg-[#22B6B2]/15 text-[#5fd6d2] text-xs font-bold uppercase tracking-wide mb-6">
                  Ahora · pedidos desde tu menú
                </span>
                <div className="space-y-3">
                  <div className="max-w-[85%] ml-auto bg-[#d9fdd3] rounded-2xl rounded-tr-sm px-4 py-2.5 text-sm text-[#2C4358]">
                    ¡Hola! Ordena directo desde nuestro menú 👇<br />
                    <span className="text-[#1a9e4b] underline font-medium">servicioatiempo.app/menu</span>
                  </div>
                  {[
                    { id: '#128', items: '2× Tacos al pastor · sin cebolla', status: 'Confirmado' },
                    { id: '#129', items: '1× Especial del día + refresco', status: 'En preparación' },
                  ].map((order) => (
                    <div key={order.id} className="bg-white/10 backdrop-blur rounded-2xl px-4 py-3 border border-white/10">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-bold text-sm">Pedido {order.id}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#22B6B2]/20 text-[#5fd6d2]">
                          {order.status}
                        </span>
                      </div>
                      <p className="text-[#9db2bf] text-sm">{order.items}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm text-[#5fd6d2] font-medium">
                  <span className="w-5 h-5">{Icons.check}</span>
                  Cada pedido llega completo, claro y en orden
                </div>
              </div>
            </Reveal>
          </div>

          {/* Bullets + aclaración */}
          <Reveal delay={200}>
            <div className="max-w-3xl mx-auto text-center">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
                {[
                  'Comparte tu menú con un solo enlace',
                  'Tus clientes arman su pedido solos',
                  'Tu equipo solo prepara y entrega',
                ].map((point) => (
                  <div key={point} className="flex items-start gap-2.5 text-left">
                    <span className="w-5 h-5 mt-0.5 text-[#189e9a] flex-shrink-0">{Icons.check}</span>
                    <span className="text-[#2C4358] font-medium text-sm leading-snug">{point}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#7b8b99] mb-8">
                No es una app de delivery ni cobra comisiones por pedido: es tu propio canal para
                recibir y organizar pedidos, <span className="font-semibold text-[#2C4358]">como un WhatsApp avanzado</span> para tu restaurante.
              </p>
              {!user && (
                <button
                  onClick={() => {
                    trackClickCta(
                      'whatsapp_compartir_menu',
                      'home_whatsapp',
                      '/login?returnUrl=/pricing&role=owner'
                    );
                    navigate('/login?returnUrl=/pricing&role=owner');
                  }}
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-[#22B6B2] hover:bg-[#2cd0cb] text-[#0d1b26] font-bold rounded-full shadow-xl shadow-[#22B6B2]/30 hover:shadow-[#22B6B2]/50 transform hover:-translate-y-0.5 transition-all duration-300"
                >
                  Quiero compartir mi menú
                  <span className="w-4 h-4 transition-transform group-hover:translate-x-1">{Icons.arrowRight}</span>
                </button>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ======================== CÓMO FUNCIONA ======================== */}
      <section id="como-funciona" className="py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#22B6B2]/10 text-[#189e9a] text-sm font-semibold mb-4">
                Así de simple
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#2C4358] mb-5">
                ¿Cómo funciona?
              </h2>
              <p className="text-xl text-[#7b8b99] max-w-2xl mx-auto">
                Tu restaurante digitalizado en solo 3 pasos.
              </p>
            </div>
          </Reveal>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {/* Línea conectora (solo desktop) */}
            <div className="hidden md:block absolute top-10 left-[16.66%] right-[16.66%] h-px bg-gradient-to-r from-[#22B6B2]/0 via-[#22B6B2]/40 to-[#22B6B2]/0" />

            {STEPS.map((step, i) => (
              <Reveal key={step.title} delay={i * 150}>
                <div className="relative text-center group">
                  <div className="relative w-20 h-20 mx-auto mb-8">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#22B6B2] to-[#2C4358] rounded-2xl rotate-6 opacity-20 group-hover:rotate-12 transition-transform duration-300" />
                    <div className="relative w-20 h-20 bg-gradient-to-br from-[#22B6B2] to-[#1d8a87] rounded-2xl flex items-center justify-center shadow-lg shadow-[#22B6B2]/25 group-hover:scale-105 transition-transform duration-300">
                      <span className="text-3xl text-white font-extrabold">{i + 1}</span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-[#2C4358] mb-3">{step.title}</h3>
                  <p className="text-[#7b8b99] leading-relaxed max-w-xs mx-auto">{step.description}</p>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Demo en vivo */}
          <Reveal delay={300}>
            <div className="mt-20 max-w-3xl mx-auto">
              <div className="relative overflow-hidden bg-gradient-to-br from-[#0d1b26] to-[#2C4358] rounded-3xl p-8 md:p-10 text-center shadow-xl shadow-[#2C4358]/20">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-[#22B6B2]/10 blur-[60px] pointer-events-none" />
                <span className="inline-block px-4 py-1.5 rounded-full bg-[#22B6B2]/15 text-[#5fd6d2] text-sm font-semibold mb-4">
                  Demo en vivo
                </span>
                <h3 className="text-2xl md:text-3xl font-extrabold text-white mb-3">
                  No nos creas a nosotros, pruébalo tú mismo
                </h3>
                <p className="text-[#9db2bf] mb-8 max-w-xl mx-auto">
                  Abre el menú de nuestro restaurante demo y vive la experiencia exacta que tendrán
                  tus clientes al recibir tu enlace por WhatsApp.
                </p>
                <a
                  href={DEMO_MENU_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackClickCta('demo_menu_live', 'home_como_funciona', DEMO_MENU_URL)}
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-[#22B6B2] hover:bg-[#2cd0cb] text-[#0d1b26] font-bold rounded-full shadow-xl shadow-[#22B6B2]/30 hover:shadow-[#22B6B2]/50 transform hover:-translate-y-0.5 transition-all duration-300"
                >
                  Ver menú demo en vivo
                  <span className="w-4 h-4 transition-transform group-hover:translate-x-1">{Icons.arrowRight}</span>
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ========================== FUNCIONES ========================== */}
      <section id="funciones" className="py-24 bg-white scroll-mt-20 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-[#22B6B2]/5 blur-[120px] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#22B6B2]/10 text-[#189e9a] text-sm font-semibold mb-4">
                Plataforma completa
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#2C4358] mb-5">
                Todo lo que necesitas
              </h2>
              <p className="text-xl text-[#7b8b99] max-w-2xl mx-auto">
                Herramientas diseñadas para modernizar cada área de tu restaurante.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature, i) => (
              <Reveal
                key={feature.title}
                delay={(i % 4) * 100}
                className={feature.highlight ? 'md:col-span-2' : ''}
              >
                <div
                  className={`h-full rounded-3xl p-7 border transition-all duration-300 group hover:-translate-y-1 ${
                    feature.highlight
                      ? 'bg-gradient-to-br from-[#0d1b26] to-[#2C4358] border-transparent shadow-xl shadow-[#2C4358]/20 hover:shadow-2xl hover:shadow-[#2C4358]/30'
                      : 'bg-[#f6f8fa] border-[#e6ebf0] hover:border-[#22B6B2]/40 hover:bg-white hover:shadow-xl hover:shadow-[#22B6B2]/10'
                  }`}
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110 ${
                      feature.highlight
                        ? 'bg-[#22B6B2]/20 text-[#5fd6d2]'
                        : 'bg-[#22B6B2]/10 text-[#189e9a]'
                    }`}
                  >
                    <span className="w-6 h-6">{feature.icon}</span>
                  </div>
                  <h3 className={`text-lg font-bold mb-3 ${feature.highlight ? 'text-white' : 'text-[#2C4358]'}`}>
                    {feature.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${feature.highlight ? 'text-[#9db2bf]' : 'text-[#7b8b99]'}`}>
                    {feature.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ========================== BENEFICIOS ========================== */}
      <section id="beneficios" className="py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#22B6B2]/10 text-[#189e9a] text-sm font-semibold mb-4">
                Resultados reales
              </span>
              <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-[#2C4358] mb-5">
                Beneficios que transforman
              </h2>
              <p className="text-xl text-[#7b8b99] max-w-2xl mx-auto">
                Descubre por qué restaurantes exitosos eligen Servicio a Tiempo.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((benefit, i) => (
              <Reveal key={benefit.title} delay={(i % 3) * 120}>
                <div className="h-full bg-white rounded-3xl p-8 border border-[#e6ebf0] hover:border-[#22B6B2]/40 hover:shadow-xl hover:shadow-[#22B6B2]/10 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-12 h-12 rounded-xl bg-[#22B6B2]/10 text-[#189e9a] flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                      <span className="w-6 h-6">{benefit.icon}</span>
                    </div>
                    <span className="text-3xl font-extrabold bg-gradient-to-r from-[#22B6B2] to-[#2C4358] bg-clip-text text-transparent">
                      {benefit.stat}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-[#2C4358] mb-2">{benefit.title}</h3>
                  <p className="text-[#7b8b99] leading-relaxed text-sm">{benefit.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <Testimonials /> */}

      {/* ========================== CTA FINAL ========================== */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-[#0d1b26] px-6 py-20 md:px-16 text-center shadow-2xl shadow-[#2C4358]/30">
              {/* Glows */}
              <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#22B6B2]/20 blur-[130px] pointer-events-none" />
              <div
                className="absolute inset-0 opacity-30 pointer-events-none"
                style={{
                  backgroundImage:
                    'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
                  backgroundSize: '48px 48px',
                  maskImage: 'radial-gradient(ellipse 70% 70% at 50% 0%, black, transparent)',
                  WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 0%, black, transparent)',
                }}
              />

              <div className="relative">
                <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-6">
                  ¿Listo para transformar
                  <span className="block bg-gradient-to-r from-[#22B6B2] to-[#4fd8d4] bg-clip-text text-transparent pb-1">
                    tu restaurante?
                  </span>
                </h2>
                <p className="text-lg md:text-xl text-[#9db2bf] mb-10 max-w-2xl mx-auto">
                  Únete a los restaurantes que ya están digitalizando su operación con Servicio a Tiempo.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  {user ? (
                    <div className="flex flex-col sm:flex-row gap-4">
                      <button
                        onClick={() => navigate('/profile')}
                        className="group inline-flex items-center gap-2 px-8 py-4 bg-[#22B6B2] hover:bg-[#2cd0cb] text-[#0d1b26] font-bold rounded-full shadow-xl shadow-[#22B6B2]/30 hover:shadow-[#22B6B2]/50 transform hover:-translate-y-0.5 transition-all duration-300"
                      >
                        Ir a Perfil
                        <span className="w-4 h-4 transition-transform group-hover:translate-x-1">{Icons.arrowRight}</span>
                      </button>
                      {tableData && (
                        <button
                          onClick={() => navigate('/menu')}
                          className="px-8 py-4 bg-white/5 text-white font-semibold rounded-full border border-white/15 hover:bg-white/10 hover:border-[#22B6B2]/50 backdrop-blur transition-all duration-300"
                        >
                          🍽️ Ver Menú
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <button
                        onClick={() => navigate('/login?returnUrl=/pricing&role=owner')}
                        className="group inline-flex items-center gap-2 px-8 py-4 bg-[#22B6B2] hover:bg-[#2cd0cb] text-[#0d1b26] font-bold rounded-full shadow-xl shadow-[#22B6B2]/30 hover:shadow-[#22B6B2]/50 transform hover:-translate-y-0.5 transition-all duration-300"
                      >
                        Soy Restaurante
                        <span className="w-4 h-4 transition-transform group-hover:translate-x-1">{Icons.arrowRight}</span>
                      </button>
                      <button
                        onClick={() => navigate('/login?role=comensal')}
                        className="px-8 py-4 bg-white/5 text-white font-semibold rounded-full border border-white/15 hover:bg-white/10 hover:border-[#22B6B2]/50 backdrop-blur transition-all duration-300"
                      >
                        Soy Comensal
                      </button>
                    </>
                  )}
                </div>
                {!user && (
                  <button
                    onClick={() => navigate('/login')}
                    className="mt-6 text-sm text-[#9db2bf] hover:text-white transition-colors underline underline-offset-4 decoration-[#22B6B2]/50"
                  >
                    ¿Ya tienes cuenta? Inicia sesión
                  </button>
                )}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============================ FOOTER ============================ */}
      <footer className="border-t border-[#e6ebf0] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <img
                src="/assets/ots/nobackground/Servicio_a_Tiempo_icon_64x64.png"
                alt="Servicio a Tiempo"
                className="w-8 h-8 object-contain"
              />
              <span className="font-bold text-[#2C4358]">
                Servicio <span className="text-[#189e9a]">a Tiempo</span>
              </span>
            </div>

            <nav className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-[#7b8b99]">
              <button onClick={() => navigate('/pricing')} className="hover:text-[#2C4358] transition-colors">
                Precios
              </button>
              <button onClick={() => navigate('/terminos')} className="hover:text-[#2C4358] transition-colors">
                Términos y Condiciones
              </button>
              <button onClick={() => navigate('/privacidad')} className="hover:text-[#2C4358] transition-colors">
                Aviso de Privacidad
              </button>
              <button onClick={() => navigate('/login')} className="hover:text-[#2C4358] transition-colors">
                Iniciar sesión
              </button>
            </nav>

            <p className="text-sm text-[#a3b1bd]">
              © {new Date().getFullYear()} Servicio a Tiempo
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
